
import { computed, defineComponent, PropType, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import BaseTree from '@/views/dashboard/_components/group-tree/base-tree.vue'
import { Group } from '@/views/dashboard/_type/group'
import {
  TerminalAuthService,
  TerminalItem,
} from '@/views/dashboard/_components/terminal-auth/terminal-auth.service'
import Axios from 'axios'
import { ServiceCommonService } from '@/views/dashboard/service/service-common.service'
import { ElMessage, ElMessageBox } from 'element-plus'
import { GroupRefreshManage } from '@/services/group'
import IPColumn from '@/views/dashboard/_components/ip-column/IPColumn.vue'
import { ServicePermissionService } from '@/services/service-permission.service'

export default defineComponent({
  components: { IPColumn, BaseTree },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    terminalType: {
      type: Array as PropType<number[]>,
      default: () => ([] as number[])
    },
    terminalApiUrl: {
      type: String,
      default: ''
    },
    permissionNames: {
      type: Array as PropType<string[]>,
      default: () => ([] as string[])
    },
  },

  setup (props, context) {
    const { t, locale } = useI18n()

    const displayTitle = computed(() => {
      void locale.value
      return props.title || t('terminalAuth.defaultTitle')
    })

    const injector = useReflectiveInjector([TerminalAuthService, ServiceCommonService])
    const service = injector.get(TerminalAuthService)
    const serviceCommonService = injector.get(ServiceCommonService)
    const servicePermissionService = injector.get(ServicePermissionService)
    const groupRefreshManage = injector.get(GroupRefreshManage)

    const tableNode = ref()
    const isLoading = ref(false)
    const activeTab = ref('authorizedView')
    const tableData = ref<TerminalItem[]>([])
    const currentGroup = ref({} as Group)
    const selectionData = ref<TerminalItem[]>([])
    const selectionShot = ref<TerminalItem[]>([])
    const isSelectAll = ref(false)
    const isShowSelection = ref(false)
    let getDataCancel

    const searchParams = reactive({
      searchKey: '',
    })

    const pagination = reactive({
      currentPage: 1,
      pageSize: 20,
      total: 0,
    })

    const isAuthorizedView = computed(() => {
      return activeTab.value === 'authorizedView'
    })

    const selectedCount = computed(() => {
      if (isSelectAll.value) {
        return currentGroup.value.total
      }
      return selectionData.value.length
    })

    function handleClose () {
      context.emit('update:modelValue', false)
    }

    function updateData () {
      getDataCancel && getDataCancel()
      isLoading.value = true
      service.getTerminalList({
        key_word: searchParams.searchKey,
        page: pagination.currentPage,
        limit: pagination.pageSize,
        group_id: currentGroup.value.group_id,
        is_auth: isAuthorizedView.value,
        permission_names: props.permissionNames,
        system_types: props.terminalType
      }, props.terminalApiUrl, {
        cancelToken: new Axios.CancelToken((callback) => getDataCancel = callback)
      }).then((res) => {
        tableData.value = res.terminal_list
        pagination.total = +res.total || 0
        isLoading.value = false
        isSelectAll.value && doSelectAll()
      }).catch((error) => {
        if (error.message) {
          isLoading.value = false
        }
      })
    }

    function updateSelectionShow () {
      const { pageSize, currentPage } = pagination
      tableData.value = selectionShot.value.slice(pageSize * (currentPage - 1), pageSize * currentPage)
    }

    function terminalGroupSelected (row: Group) {
      currentGroup.value = row
      handleCancelSelect()
    }

    function handleSizeChange (value) {
      pagination.pageSize = value
      pagination.currentPage = 1
      if (isShowSelection.value && !isSelectAll.value) {
        updateSelectionShow()
      } else {
        updateData()
      }
    }

    function handleCurrentChange (value) {
      pagination.currentPage = value
      if (isShowSelection.value && !isSelectAll.value) {
        updateSelectionShow()
      } else {
        updateData()
      }
    }

    function handleSearch () {
      pagination.currentPage = 1
      updateData()
    }

    function handleSelectAll () {
      handleCancelSelect()
      isSelectAll.value = true
      doSelectAll()
    }

    function doSelectAll () {
      tableData.value.forEach(row => tableNode.value?.toggleRowSelection(row))
    }

    function handleCancelSelect () {
      tableNode.value.clearSelection()
      isSelectAll.value = false
      toggleShowSelected(false)
    }

    function handleSelectionChange (values: TerminalItem[]) {
      selectionData.value = values
    }

    /**
     * 切换展示已选数据
     * @param show 如果使用了show参数，则是切换到指定状态。不设置表示切换反状态
     */
    function toggleShowSelected (show?: boolean) {
      if (show !== isShowSelection.value) {
        pagination.currentPage = 1
      }
      isShowSelection.value = typeof show === 'boolean' ? show : !isShowSelection.value
      if (isShowSelection.value && !isSelectAll.value) {
        pagination.total = selectionData.value.length
        selectionShot.value = [...selectionData.value]
        updateSelectionShow()
      } else {
        updateData()
      }
    }

    function handleItemAuth (item: TerminalItem) {
      ElMessageBox.confirm(
        isAuthorizedView.value ? t('terminalAuth.confirmCancel') : t('terminalAuth.confirmAdd'),
        t('cmtable.tip')
      ).then(() => {
        servicePermissionService.authTerminal({
          permission_names: props.permissionNames,
          device_ids: [item.device_id],
          is_auth: !isAuthorizedView.value
        }).then(() => {
          ElMessage.success(t('common.success'))
          tableNode.value?.toggleRowSelection(item, false)
          groupRefreshManage.refreshGroup()
          updateData()
          context.emit('success')
        })
      })
    }

    function handleBatchOperate () {
      ElMessageBox.confirm(
        isAuthorizedView.value ? t('terminalAuth.confirmCancel') : t('terminalAuth.confirmAdd'),
        t('cmtable.tip')
      ).then(() => {
        servicePermissionService.authTerminal({
          permission_names: props.permissionNames,
          device_ids: isSelectAll.value ? undefined : selectionData.value.map(v => v.device_id),
          group_id: isSelectAll.value ? currentGroup.value.group_id : undefined,
          is_auth: !isAuthorizedView.value
        }).then(() => {
          ElMessage.success(t('common.success'))
          handleCancelSelect()
          groupRefreshManage.refreshGroup()
          updateData()
          context.emit('success')
        })
      })
    }

    watch(() => props.modelValue, (value) => {
      if (!value) {
        activeTab.value = 'authorizedView'
        searchParams.searchKey = ''
        handleCancelSelect()
      }
    })

    return {
      displayTitle,
      activeTab,
      tableNode,
      tableData,
      isLoading,
      searchParams,
      pagination,
      isSelectAll,
      isShowSelection,
      selectionData,
      selectedCount,
      isAuthorizedView,
      updateData,
      handleItemAuth,
      terminalGroupSelected,
      handleSizeChange,
      handleCurrentChange,
      handleSearch,
      handleSelectAll,
      handleCancelSelect,
      handleSelectionChange,
      toggleShowSelected,
      handleClose,
      handleBatchOperate,
    }
  }
})
